/**
 * calendarViewToggle.js - Calendar/List View Toggle Handler
 *
 * DESCRIPTION:
 * ES6 Module that manages toggling between calendar and list views.
 * Uses state-based rendering where a single state variable tracks the current view,
 * and all DOM updates are driven by that state.
 *
 * BEHAVIOR:
 * - Initial state is detected from server-rendered DOM state
 * - Buttons switch between 'calendar' and 'list'
 * - Button visibility: shows button for view you can switch TO (not current view)
 *
 * NOTE:
 * - listSection may not exist when there are 0 filtered events. In that case,
 *   list view is disabled and calendar view becomes the fallback.
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

class CalendarViewToggleHandler {
  constructor() {
    this.DEBUG = false;

    this.errorHandler = new ErrorHandler({
      module: 'CalendarViewToggleHandler',
      maxErrors: 15
    });

    this.isInitialized = false;

    // State variable: 'list' | 'calendar'
    this.currentView = 'list';

    // Internal bookkeeping
    this._onToCalendarClick = null;
    this._onToListClick = null;
    this._renderScheduled = false;
    this._renderTimer = null;
    this._raf1 = null;
    this._raf2 = null;
  }

  _log(...args) {
    if (this.DEBUG) console.log('[CalendarViewToggleHandler]', ...args);
  }

  initialize() {
    if (!isBrowser) return false;
    if (this.isInitialized) return true;

    try {
      // DOM references
      this.toCalendarBtn = document.querySelector('.calendar-selection button');
      this.toListBtn = document.querySelector('.calendar-list-selection button');
      this.calendarSection = document.querySelector('.calendar-calendar');
      this.listSection = document.querySelector('.calendar-list');
      this.paginationContainer = document.querySelector('.pagination-container');
      this.calendarButtonContainer = document.querySelector('.calendar-selection');
      this.listButtonContainer = document.querySelector('.calendar-list-selection');
      this.viewStateInput = document.querySelector('#view-state-input'); // Hidden form input

      this._log('initialize()', {
        toCalendarBtn: !!this.toCalendarBtn,
        toListBtn: !!this.toListBtn,
        calendarSection: !!this.calendarSection,
        listSection: !!this.listSection,
        paginationContainer: !!this.paginationContainer
      });

      // Required core elements: buttons and calendar section
      if (!this.toCalendarBtn || !this.toListBtn || !this.calendarSection) {
        this.errorHandler.logError(
          'Toggle elements missing',
          new Error('Required core elements not found (buttons or calendar section)')
        );
        return false;
      }

      // listSection may not exist if there are 0 filtered events
      if (!this.listSection) {
        this._log('List section not found - list view will be disabled');
      }

      // Detect current view from server-rendered DOM state:
      // If calendar section doesn't have 'hide' class, we're in calendar view
      this.currentView = this.calendarSection.classList.contains('hide') ? 'list' : 'calendar';
      this._log('Detected view from DOM:', this.currentView);

      // Bind handlers so we can remove them later
      this._onToCalendarClick = (e) => {
        e.preventDefault();
        this.setState('calendar');
      };

      this._onToListClick = (e) => {
        e.preventDefault();
        this.setState('list');
      };

      this.toCalendarBtn.addEventListener('click', this._onToCalendarClick);
      this.toListBtn.addEventListener('click', this._onToListClick);

      // Ensure UI matches detected view
      this._render();

      this.errorHandler.logSuccess('Initialized toggle handler', { module: 'initialization' });
      this.isInitialized = true;
      return true;
    } catch (err) {
      this.errorHandler.handleFatalError('Initialization error', err, { module: 'initialization' });
      return false;
    }
  }

  /**
   * setState - Updates the view state and renders accordingly
   * @param {'list'|'calendar'} newView
   */
  setState(newView) {
    if (!['list', 'calendar'].includes(newView)) {
      this.errorHandler.logError('Invalid view state', new Error(`Invalid view: ${newView}`), { newView });
      return;
    }

    if (newView === this.currentView) {
      this._log('Already in view:', newView);
      return;
    }

    // If list view requested but listSection doesn't exist, refuse and keep calendar
    if (newView === 'list' && !this.listSection) {
      this._log('List view requested but list section missing; keeping calendar view.');
      this.currentView = 'calendar';
      this._render();
      return;
    }

    this._log('State change:', this.currentView, '→', newView);
    this.currentView = newView;
    this._render();
  }

  /**
   * _render - Updates DOM based on currentView state
   * NOTE: No recursion; render handles fallback paths directly.
   */
  _render() {
    this._log('render()', this.currentView);

    const view = this.currentView;

    if (view === 'list') {
      // List view requires listSection; setState already guards this.
      this._showElement(this.listSection);

      this._hideElement(this.calendarSection);
      this._showElement(this.calendarButtonContainer); // show "Calendar View" button
      this._hideElement(this.listButtonContainer); // hide "List View" button
      this._showElement(this.paginationContainer);

      this._setButtonState('list');
    }

    if (view === 'calendar') {
      if (this.listSection) this._hideElement(this.listSection);

      this._showElement(this.calendarSection);
      this._hideElement(this.calendarButtonContainer); // hide "Calendar View" button
      this._showElement(this.listButtonContainer); // show "List View" button
      this._hideElement(this.paginationContainer);

      this._setButtonState('calendar');

      // Trigger calendar rendering after visibility change
      this._scheduleCalendarRender();
    }

    // Update hidden input
    this._updateFormViewInput();
  }

  _showElement(el) {
    if (!el) return;
    el.classList.remove('hide');
  }

  _hideElement(el) {
    if (!el) return;
    el.classList.add('hide');
  }

  /**
   * _setButtonState - Set aria-pressed attributes
   * (kept as-is; could be upgraded to tabs semantics later)
   */
  _setButtonState(activeView) {
    if (!this.toCalendarBtn || !this.toListBtn) return;

    if (activeView === 'calendar') {
      this.toCalendarBtn.setAttribute('aria-pressed', 'true');
      this.toListBtn.setAttribute('aria-pressed', 'false');
    } else {
      this.toListBtn.setAttribute('aria-pressed', 'true');
      this.toCalendarBtn.setAttribute('aria-pressed', 'false');
    }
  }

  _updateFormViewInput() {
    if (!this.viewStateInput) return;
    this.viewStateInput.value = this.currentView;
    this._log('Updated hidden input view-state-input:', this.currentView);
  }

  /**
   * Schedule FullCalendar to re-render when switching to calendar view.
   * - Uses requestAnimationFrame (twice) to wait for layout/paint
   * - Cancelable (clears previous scheduled renders)
   */
  _scheduleCalendarRender() {
    // Cancel any pending render work
    this._cancelScheduledRender();

    // If destroyed before frames run, no-op
    this._renderScheduled = true;

    this._raf1 = window.requestAnimationFrame(() => {
      this._raf2 = window.requestAnimationFrame(async () => {
        this._renderScheduled = false;

        // Only render if we are still in calendar view and initialized
        if (!this.isInitialized || this.currentView !== 'calendar') return;

        try {
          if (window.calendarRenderWhenVisible) {
            this._log('Calling window.calendarRenderWhenVisible()');
            await window.calendarRenderWhenVisible();
          } else if (window.CalendarApp?.render) {
            this._log('Calling window.CalendarApp.render()');
            await window.CalendarApp.render();
          } else {
            this._log('No calendar render method available');
          }
        } catch (e) {
          this.errorHandler.logError('Error during calendar render', e, { module: 'calendar-render' });
        }
      });
    });
  }

  _cancelScheduledRender() {
    if (!isBrowser) return;

    if (this._raf1) {
      window.cancelAnimationFrame(this._raf1);
      this._raf1 = null;
    }
    if (this._raf2) {
      window.cancelAnimationFrame(this._raf2);
      this._raf2 = null;
    }

    if (this._renderTimer) {
      clearTimeout(this._renderTimer);
      this._renderTimer = null;
    }

    this._renderScheduled = false;
  }

  destroy() {
    if (!this.isInitialized) return;

    // Remove event listeners
    if (this.toCalendarBtn && this._onToCalendarClick) {
      this.toCalendarBtn.removeEventListener('click', this._onToCalendarClick);
    }
    if (this.toListBtn && this._onToListClick) {
      this.toListBtn.removeEventListener('click', this._onToListClick);
    }

    this._onToCalendarClick = null;
    this._onToListClick = null;

    // Cancel any pending render work
    this._cancelScheduledRender();

    this.isInitialized = false;
    this._log('destroy() complete');
  }

  getCurrentView() {
    return this.currentView;
  }
}

const toggleHandler = new CalendarViewToggleHandler();

// Only initialize on calendar pages
if (isBrowser) {
  document.addEventListener('DOMContentLoaded', () => {
    // More targeted gating to avoid noise on other pages:
    // Require at least the calendarSection container and the button containers to exist.
    const hasCalendar = !!document.querySelector('.calendar-calendar');
    const hasToggleButtons =
      !!document.querySelector('.calendar-selection button') &&
      !!document.querySelector('.calendar-list-selection button');

    if (hasCalendar && hasToggleButtons) {
      toggleHandler.initialize();
    }
  });
}

export default toggleHandler;
